import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, firstValueFrom, map } from 'rxjs';
import { environment } from 'src/environments/environment';
import { PersonaRepository } from 'src/app/domain/service/persona/persona-repository';
import { Persona, PersonaFiltro } from 'src/app/domain/model/persona/persona';
import { PageResponse } from 'src/app/domain/model/common/page-response.dto';
import { validarSize } from 'src/app/common/validation-utils';

/**
 * Tamaños que el backend aplicaba por defecto antes de volver `size` obligatorio
 * (seguridad-girop §2.3). Se envían explícitos para no cambiar el comportamiento.
 */
const SIZE_LISTAR_DEFECTO = 10;
const SIZE_BUSCAR_DEFECTO = 20;

/**
 * Adaptador HTTP: HttpPersonaRepository
 *
 * Implementa el puerto PersonaRepository para comunicación HTTP con backend.
 * Realiza llamadas REST al backend Spring Boot (PersonaController).
 *
 * Arquitectura Hexagonal:
 * - Este adaptador está en infraestructura
 * - Implementa el puerto PersonaRepository del dominio
 * - Trabaja con la entidad de dominio Persona
 *
 * @author Emerson Rodriguez - Ingeneo
 */
@Injectable({ providedIn: 'root' })
export class HttpPersonaRepository implements PersonaRepository {

  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.backend.baseUrl}/servicios/personas`;

  /**
   * Obtiene todas las personas (extrae content del PageResponse)
   */
  getAll(): Promise<Persona[]> {
    const params = new HttpParams().set('size', String(SIZE_LISTAR_DEFECTO));
    return firstValueFrom(
      this.http.get<PageResponse<Persona>>(this.baseUrl, { params }).pipe(
        map(response => response.content)
      )
    );
  }

  /**
   * Obtiene una persona por ID
   */
  getById(cdpersona: number): Promise<Persona> {
    return firstValueFrom(
      this.http.get<Persona>(`${this.baseUrl}/${cdpersona}`)
    );
  }

  /**
   * Busca persona por tipo y número de identificación
   */
  getByIdentificacion(tipoId: string, numeroId: string): Promise<Persona | null> {
    const params = new HttpParams()
      .set('tipoId', tipoId)
      .set('numeroId', numeroId);
    return firstValueFrom(
      this.http.get<Persona>(`${this.baseUrl}/identificacion`, { params })
    ).catch(() => null);
  }

  /**
   * Verifica si existe una persona con la identificación dada
   */
  exists(tipoId: string, numeroId: string): Promise<boolean> {
    return this.getByIdentificacion(tipoId, numeroId).then(p => p !== null);
  }

  /**
   * Crea una nueva persona
   */
  create(persona: Persona): Promise<Persona> {
    return firstValueFrom(
      this.http.post<Persona>(this.baseUrl, persona)
    );
  }

  /**
   * Actualiza una persona existente
   */
  update(cdpersona: number, persona: Persona): Promise<Persona> {
    return firstValueFrom(
      this.http.put<Persona>(`${this.baseUrl}/${cdpersona}`, persona)
    );
  }

  /**
   * Elimina (inactiva) una persona por ID
   */
  delete(cdpersona: number): Promise<void> {
    return firstValueFrom(
      this.http.delete<void>(`${this.baseUrl}/${cdpersona}`)
    );
  }

  /**
   * Busca personas por nombre (nombre o apellido)
   */
  searchByNombre(nombre: string): Promise<Persona[]> {
    const params = new HttpParams()
      .set('nombre', nombre)
      .set('size', String(SIZE_BUSCAR_DEFECTO));
    return firstValueFrom(
      this.http.get<PageResponse<Persona>>(`${this.baseUrl}/buscar`, { params }).pipe(
        map(response => response.content)
      )
    );
  }

  /**
   * Obtiene solo las personas activas
   */
  getActivos(): Promise<Persona[]> {
    return firstValueFrom(
      this.http.get<Persona[]>(`${this.baseUrl}/activos`)
    );
  }

  /**
   * Cuenta el total de personas
   */
  count(): Promise<number> {
    return firstValueFrom(
      this.http.get<number>(`${this.baseUrl}/count`)
    );
  }

  // ============================================
  // Métodos auxiliares para uso con Observables
  // ============================================

  /**
   * Obtiene todas las personas (Observable)
   */
  getAll$(): Observable<Persona[]> {
    const params = new HttpParams().set('size', String(SIZE_LISTAR_DEFECTO));
    return this.http.get<PageResponse<Persona>>(this.baseUrl, { params }).pipe(
      map(response => response.content)
    );
  }

  /**
   * Obtiene personas paginadas con cursor
   */
  getPage(cursor?: string, size: number = 10): Promise<PageResponse<Persona>> {
    validarSize(size);
    let params = new HttpParams().set('size', size.toString());
    if (cursor) {
      params = params.set('cursor', cursor);
    }
    return firstValueFrom(
      this.http.get<PageResponse<Persona>>(this.baseUrl, { params })
    );
  }

  /**
   * Obtiene personas paginadas con filtros de búsqueda
   * Envía los filtros al endpoint GET /api/personas con query params
   */
  getPageWithFilters(cursor?: string, size: number = 10, filtro?: PersonaFiltro): Promise<PageResponse<Persona>> {
    return this.getPageWithFiltersAndDirection(cursor, size, filtro, 'NEXT');
  }

  /**
   * Obtiene personas paginadas con filtros y dirección específica
   * @param direction 'NEXT' para siguiente página, 'PREVIOUS' para anterior
   */
  getPageWithFiltersAndDirection(
    cursor?: string,
    size: number = 10,
    filtro?: PersonaFiltro,
    direction: 'NEXT' | 'PREVIOUS' = 'NEXT'
  ): Promise<PageResponse<Persona>> {
    validarSize(size);
    let params = new HttpParams().set('size', size.toString()).set('direction', direction);
    if (cursor) {
      params = params.set('cursor', cursor);
    }
    // Agregar filtros solo si tienen valor
    if (filtro) {
      if (filtro.tipoId) params = params.set('tipoId', filtro.tipoId);
      if (filtro.numId) params = params.set('numId', filtro.numId);
      if (filtro.nombre1) params = params.set('nombre1', filtro.nombre1);
      if (filtro.nombre2) params = params.set('nombre2', filtro.nombre2);
      if (filtro.apellido1) params = params.set('apellido1', filtro.apellido1);
      if (filtro.apellido2) params = params.set('apellido2', filtro.apellido2);
      if (filtro.nombreComercial) params = params.set('nombreComercial', filtro.nombreComercial);
      if (filtro.activo) params = params.set('activo', filtro.activo);
    }
    return firstValueFrom(
      this.http.get<PageResponse<Persona>>(this.baseUrl, { params })
    );
  }

  /**
   * Cuenta personas con filtros de búsqueda
   */
  countWithFilters(filtro?: PersonaFiltro): Promise<number> {
    let params = new HttpParams();
    if (filtro) {
      if (filtro.tipoId) params = params.set('tipoId', filtro.tipoId);
      if (filtro.numId) params = params.set('numId', filtro.numId);
      if (filtro.nombre1) params = params.set('nombre1', filtro.nombre1);
      if (filtro.nombre2) params = params.set('nombre2', filtro.nombre2);
      if (filtro.apellido1) params = params.set('apellido1', filtro.apellido1);
      if (filtro.apellido2) params = params.set('apellido2', filtro.apellido2);
      if (filtro.nombreComercial) params = params.set('nombreComercial', filtro.nombreComercial);
      if (filtro.activo) params = params.set('activo', filtro.activo);
    }
    return firstValueFrom(
      this.http.get<number>(`${this.baseUrl}/count`, { params })
    );
  }

  /**
   * Obtiene la última página de personas
   * Llama al endpoint GET /api/personas/ultima
   */
  getLastPage(size: number = 10, filtro?: PersonaFiltro): Promise<PageResponse<Persona>> {
    validarSize(size);
    let params = new HttpParams().set('size', size.toString());
    if (filtro) {
      if (filtro.tipoId) params = params.set('tipoId', filtro.tipoId);
      if (filtro.numId) params = params.set('numId', filtro.numId);
      if (filtro.nombre1) params = params.set('nombre1', filtro.nombre1);
      if (filtro.apellido1) params = params.set('apellido1', filtro.apellido1);
      if (filtro.nombreComercial) params = params.set('nombreComercial', filtro.nombreComercial);
      if (filtro.activo) params = params.set('activo', filtro.activo);
    }
    return firstValueFrom(
      this.http.get<PageResponse<Persona>>(`${this.baseUrl}/ultima`, { params })
    );
  }

  /**
   * Obtiene personas paginadas con cursor (Observable)
   */
  getPage$(cursor?: string, size: number = 10): Observable<PageResponse<Persona>> {
    validarSize(size);
    let params = new HttpParams().set('size', size.toString());
    if (cursor) {
      params = params.set('cursor', cursor);
    }
    return this.http.get<PageResponse<Persona>>(this.baseUrl, { params });
  }

  /**
   * Obtiene una persona por ID (Observable)
   */
  getById$(cdpersona: number): Observable<Persona> {
    return this.http.get<Persona>(`${this.baseUrl}/${cdpersona}`);
  }

  /**
   * Crea una nueva persona (Observable)
   */
  create$(persona: Persona): Observable<Persona> {
    return this.http.post<Persona>(this.baseUrl, persona);
  }

  /**
   * Actualiza una persona (Observable)
   */
  update$(persona: Persona): Observable<Persona> {
    return this.http.put<Persona>(`${this.baseUrl}/${persona.cdpersona}`, persona);
  }

  /**
   * Elimina una persona (Observable)
   */
  delete$(cdpersona: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${cdpersona}`);
  }

  /**
   * Obtiene personas activas (Observable)
   */
  getActivos$(): Observable<Persona[]> {
    return this.http.get<Persona[]>(`${this.baseUrl}/activos`);
  }
}
